/**
 * P1-15: Microsoft Clarity, gated on analytics consent.
 *
 * Clarity records full session replays (mouse moves, clicks, scrolls, page
 * transitions). That is squarely analytics-class tracking, so it must not
 * fire until the user accepts the consent banner. Before we gated this,
 * Clarity ran on every visitor regardless of choice.
 */
'use client';

import { useEffect, useState } from 'react';
import Script from 'next/script';
import { readConsent } from '@/lib/consent/read';

export default function ClarityLoader({ clarityId, nonce }: { clarityId: string; nonce?: string }) {
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    const check = () => { if (readConsent() === 'granted') setConsented(true); };
    check();
    const idleId = window.requestIdleCallback?.(check, { timeout: 2000 })
      ?? setTimeout(check, 2000);
    const onStorage = () => check();
    window.addEventListener('storage', onStorage);
    return () => {
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(idleId as number);
      else clearTimeout(idleId as unknown as ReturnType<typeof setTimeout>);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  if (!consented) return null;

  return (
    <Script
      id="clarity-script"
      strategy="lazyOnload"
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${clarityId}");`,
      }}
    />
  );
}
